import { useEffect, useState } from 'react';

import { isSsr } from '@change-corgi/core/ssr';

import { googleMapsApiClientKey } from '#config/google';

export type UseLoadGooglePlacesReturn = {
	status: 'loading' | 'loaded' | 'error';
	autocompleteService?: google.maps.places.AutocompleteService;
	placesService?: google.maps.places.PlacesService;
};

let optionsSet = false;

async function loadGooglePlacesLibrary(): Promise<google.maps.PlacesLibrary> {
	if (isSsr()) {
		return Promise.reject(new Error('Google Places API can only be loaded on the client-side!'));
	}
	// Dynamic import avoids the top-level window.trustedTypes access in @googlemaps/js-api-loader v2.1.0+
	const { importLibrary, setOptions } = await import(
		/* webpackChunkName: "cmp/google_maps_js_api_loader" */ '@googlemaps/js-api-loader'
	);
	if (!optionsSet) {
		setOptions({
			key: googleMapsApiClientKey,
			v: 'weekly',
		});
		optionsSet = true;
	}
	return importLibrary('places');
}

export function useLoadGooglePlaces(): UseLoadGooglePlacesReturn {
	const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
	const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService>();
	const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>();

	useEffect(() => {
		(async () => {
			try {
				const placesLibrary = await loadGooglePlacesLibrary();
				setAutocompleteService(new placesLibrary.AutocompleteService());
				setPlacesService(new placesLibrary.PlacesService(document.createElement('div')));
				setStatus('loaded');
			} catch (e) {
				setStatus('error');
			}
		})();
	}, []);

	return {
		status,
		autocompleteService,
		placesService,
	};
}
